// src/domain/seedContext.ts
// Ambient context that lets the seed replay drive the real domain flows at a
// chosen past timestamp, silently, with deterministic ids. Outside replay every
// field is inert and the flows behave exactly as they do in the live app.

interface SeedContext {
  nowOverride: string | null;
  silent: boolean;
  idSource: ((prefix: string) => string) | null;
}

const ctx: SeedContext = { nowOverride: null, silent: false, idSource: null };

/** The replay timestamp, or null when not replaying (flows read the sim clock). */
export function seedNow(): string | null {
  return ctx.nowOverride;
}

/** True while replay wants side effects (activity / events / sound) suppressed. */
export function seedSilent(): boolean {
  return ctx.silent;
}

/** A deterministic id for `prefix`, or null when not replaying (flows keep own ids). */
export function seedId(prefix: string): string | null {
  return ctx.idSource ? ctx.idSource(prefix) : null;
}

export interface SeedContextOptions {
  at?: string;
  silent?: boolean;
  idSource?: (prefix: string) => string;
}

/** Run `fn` with the given seed context applied, restoring the prior context after. */
export function withSeedContext<T>(opts: SeedContextOptions, fn: () => T): T {
  const prev: SeedContext = { ...ctx };
  if (opts.at !== undefined) ctx.nowOverride = opts.at;
  if (opts.silent !== undefined) ctx.silent = opts.silent;
  if (opts.idSource !== undefined) ctx.idSource = opts.idSource;
  try {
    return fn();
  } finally {
    ctx.nowOverride = prev.nowOverride;
    ctx.silent = prev.silent;
    ctx.idSource = prev.idSource;
  }
}

/** A per-run deterministic id counter, keyed by prefix (dep-seed-0001, sale-seed-0001, ...). */
export function makeIdSource(): (prefix: string) => string {
  const counters: Record<string, number> = {};
  return (prefix: string) => {
    counters[prefix] = (counters[prefix] ?? 0) + 1;
    return `${prefix}-seed-${counters[prefix].toString().padStart(4, '0')}`;
  };
}
